import { Button, Heading, Text, Theme, useIsTheme } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { ReactNode, useEffect, useState } from 'react'
import { PaymentType, RecoverableTerminalErrorStatus } from '@afterpay/types'
import { Cohort } from '@afterpaytouch/portal-api'

import { DisabledModal, Head, ModuleContainer, NewPaymentMethod, ProfileCard } from '../../component'
import { Route, useSummaryRoute } from '../../config/router'
import { checkoutErrors, CheckoutPage, TopazError, CheckoutStatus, ExpandableComponent } from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import {
  setCheckoutError,
  setExpandedComponent,
  useAppDispatch,
  useAppSelector,
  useExpandedComponentId,
  useAppRouter,
  setCheckoutStatus,
  useIsRecoverableFlow,
  setRecoverableFlowStatus,
  setRecoverableFlowPageReady,
  useIsConsumerLendingOrder,
  useSelectedPaymentType,
  resetPreferredPaymentMethodErrors,
  useCohort,
  useIsOverduePaymentsFlow,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useSubHeadingColor } from '../../utils/convergence'
import styles from './styles.module.css'

export const AddPaymentMethodPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const dispatch = useAppDispatch()
  const { instrument } = useMonitoringWithEnduringEventProperties()
  const { headingSize, textSize, buttonSize } = useCheckoutSizes()
  const summaryRoute = useSummaryRoute()
  const isRecoverableFlow = useIsRecoverableFlow()
  const isOverduePaymentsFlow = useIsOverduePaymentsFlow()
  const isCashOnlyCohort = useCohort() === Cohort.CashOnly
  // Cash-cohort users cannot add a payment method in the recovery/overdue flow — they repay via
  // Cash App Pay. Block this page (and its Continue/submit path); if they reach it (e.g. by editing
  // the URL), boot them to a deterministic route — the overdue/risk-decline recovery page in this
  // flow, else summary (useSummaryRoute resolves this).
  const blockCashCohortPaymentMethodAdd = isCashOnlyCohort && (isRecoverableFlow || isOverduePaymentsFlow)
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const expandedComponentId = useAppSelector(useExpandedComponentId)
  const paymentType = useSelectedPaymentType() ?? ('PBI' as PaymentType)

  const isAPTheme = useIsTheme(Theme.AP)
  const subHeadingColor = useSubHeadingColor()

  const handleTerminalError = (reason: TopazError): void => {
    dispatch(
      setCheckoutError({
        error: checkoutErrors[reason],
        errorCode: reason,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const handleSubmit = async (): Promise<void> => {
    if (isRecoverableFlow) {
      dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
      dispatch(setRecoverableFlowPageReady(true))
    } else {
      dispatch(setCheckoutStatus(CheckoutStatus.Started))
    }
    dispatch(resetPreferredPaymentMethodErrors())
    // Collapse the payment method section so it doesn't appear expanded on return to summary.
    // Only clear when the payment accordion is the expanded one — otherwise we'd also collapse
    // other default-expanded sections (e.g. shipping) in non-payment summary flows.
    if (expandedComponentId === ExpandableComponent.PAYMENT_METHOD_INLINE) {
      dispatch(setExpandedComponent(null))
    }
    router.push(summaryRoute)
  }

  const handleContinue = instrument(TrackingEvent.PRESSED_ADD_FIRST_PAYMENT_CONTINUE, () => setFormSubmitted(!formSubmitted))

  const pageTitle = `${t('paymentMethod:addPaymentMethod:heading')} | ${t('common:brandName')}`
  let description: string
  let cta: string
  switch (paymentType) {
    case 'PCL':
      description = t('paymentMethod:addPaymentMethod:description:PCL')
      cta = t('paymentMethod:addPaymentMethod.cta.PCL')
      break
    case 'PAD':
      description = t('paymentMethod:addPaymentMethod:description:PAD')
      cta = t('paymentMethod:addPaymentMethod.cta.PAD')
      break
    default:
      description = t('paymentMethod:addPaymentMethod:description:PBI')
      cta = t('paymentMethod:addPaymentMethod.cta.PBI')
  }

  useEffect(() => {
    router.prefetch(Route.TERMINAL_ERROR)
  }, [])

  useEffect(() => {
    if (blockCashCohortPaymentMethodAdd) {
      router.replace(summaryRoute)
    }
  }, [blockCashCohortPaymentMethodAdd, router, summaryRoute])

  const renderSubheading = (): ReactNode => {
    if (!isAPTheme) {
      return (
        <div className={styles.subHeadingContainer}>
          <Text size={textSize} color={subHeadingColor}>
            {description}
          </Text>
        </div>
      )
    }

    return (
      <>
        <ModuleContainer nested padding={{ top: 'Large', horizontal: 'XLarge' }}>
          <ProfileCard />
        </ModuleContainer>
        <ModuleContainer nested padding={{ top: 'Large', horizontal: 'XLarge' }}>
          <Text size={textSize}>{description}</Text>
        </ModuleContainer>
      </>
    )
  }

  if (blockCashCohortPaymentMethodAdd) {
    return null
  }

  return (
    <>
      <Head title={pageTitle} />
      <Heading size={headingSize}>{t('paymentMethod:addPaymentMethod:heading')}</Heading>
      <ModuleContainer padding={{ vertical: 'Small' }} mintBorder={isAPTheme} showBorder={isAPTheme}>
        {renderSubheading()}
        <NewPaymentMethod
          onSuccess={async () => await handleSubmit()}
          onSubmit={(isSubmitting) => setIsSubmitting(isSubmitting)}
          formSubmitted={formSubmitted}
          onTerminalError={handleTerminalError}
          displayCreditCardMessage={isConsumerLendingOrder}
        />
        <ModuleContainer padding={isAPTheme ? { horizontal: 'XLarge', bottom: 'Large' } : 'None'} showBorder={isAPTheme} nested={isAPTheme}>
          <Button
            loading={isSubmitting}
            type='submit'
            padding='Fluid'
            size={buttonSize}
            disabled={isSubmitting}
            testNameSpace='add-payment-method'
            onClick={handleContinue}
          >
            {cta}
          </Button>
        </ModuleContainer>
      </ModuleContainer>
      <TrackPageViewed />
      {isSubmitting && <DisabledModal />}
    </>
  )
}
